<template>
  <section class="signup-form">
    <h1 class="form-title">{{ t('create_account_title') }}</h1>
    <v-form v-model="valid">
      <gs-input
        name="name"
        v-model="form.name"
        :label="`${t('form_label_name')}*`"
        autocomplete="name"
        :hide-details="false"
        :rules="[rules.required]"
        type="text"
      />
      <gs-input
        name="company-name"
        v-model="form.companyName"
        :label="`${t('form_label_company_name')}*`"
        autocomplete="company"
        :hide-details="false"
        :rules="[rules.required]"
        type="text"
      />
      <gs-input
        name="email"
        v-model="form.email"
        :label="`${t('form_label_email')}*`"
        autocomplete="email"
        validate-on="blur"
        :hide-details="false"
        :rules="[rules.required, rules.email]"
        @input="() => store.commit('setEmailAlreadyUsed', false)"
        type="email"
      />
      <gs-input
        name="password"
        v-model="form.password"
        class="password-field"
        :label="`${t('form_label_password')}*`"
        autocomplete="password"
        :hint="t('form_hint_password_complexity')"
        :hide-details="false"
        :rules="[rules.required, rules.password]"
        :append-inner-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
        :persistent-hint="true"
        :type="showPassword ? 'text' : 'password'"
        @click:append-inner="showPassword = !showPassword"
      />
      <div v-if="emailAlreadyUsed" class="error-message">
        <v-icon>mdi-information-outline</v-icon>
        <div>
          {{ t('form_email_alread_used') }}
          <span class="login-link" @click="$router.push('/login?app')">
            {{ $t('login') }}
          </span>
        </div>
      </div>
      <gs-input
        id="vat-number"
        name="vat-number"
        v-model="form.vatNumber"
        :label="t('form_label_vat_number')"
        autocomplete="vat"
        :hide-details="false"
        type="text"
      />
      <v-checkbox
        id="terms-and-conditions"
        name="terms-and-conditions"
        class="terms-and-conditions-input"
        v-model="form.termsAndConditions"
        density="compact"
        :hide-details="true"
        :rules="[rules.required]"
      >
        <template v-slot:label>
          <span
            v-html="
              `${t('form_label_terms_and_conditions', {
                termsAndConditionsUrl: TERMS_CONDITIONS_URL,
                privacyPolicyUrl: PRIVACY_POLICY_URL,
              })}*`
            "
          />
        </template>
      </v-checkbox>
      <AddPromoCode
        class="promo-code-trigger"
        @setPromoCode="(promoCode: string) => form.promoCode = promoCode"
      />
      <gs-button
        size="large"
        type="primary"
        full-width
        :disabled="!submitIsEnabled"
        :loading="loading"
        @click.prevent="submit"
      >
        {{ t('create_account_cta') }}
      </gs-button>
      <div class="already-member">
        {{ t('already_member') }}
        <span class="green-text" @click="navigateToLogin">{{ t('login') }}</span>
      </div>
    </v-form>
  </section>
</template>

<script setup lang="ts">
import { reactive, ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'

import AddPromoCode from '@/components/checkout/AddPromoCode.vue'
import gsInput from '@/components/form/gs-input.vue'
import { required, email, password } from '@/helpers/validation'
import { PRIVACY_POLICY_URL, TERMS_CONDITIONS_URL } from '@/helpers/constants'
import { getLocation } from '@/helpers/geolocation'
import { createAccount, usePromoCode, userRegistration } from '@api/index'
import { useRoute, useRouter } from 'vue-router'
import type { Currency } from '@/helpers/interfaces'

interface SignupFormInfo {
  name: string
  companyName: string
  email: string
  password: string
  vatNumber: string
  termsAndConditions: boolean
  promoCode?: string
}

const { t } = useI18n()
const store = useStore()
const route = useRoute()
const router = useRouter()
const emit = defineEmits<{ (e: 'registered') }>()

const valid = ref<boolean>(false)
const loading = ref<boolean>(false)
const showPassword = ref<boolean>(false)
const country = ref<string>()
getLocation()
  .then((c) => (country.value = c))
  .catch((e) => console.error("Error trying to retrieve the user's country", e))

store.commit('setEmailAlreadyUsed', false)
store.commit('setSignupComplete', false)
const emailAlreadyUsed = computed<boolean>(() => store.getters['getAccount'].emailAlreadyUsed)
const submitIsEnabled = computed<boolean>(
  () => valid.value && !emailAlreadyUsed.value && !loading.value,
)
const rules = {
  required,
  email,
  password,
}

const name =
  store.getters['getCurrentUser'].firstName || store.getters['getCurrentUser'].lastName
    ? `${store.getters['getCurrentUser'].firstName} ${store.getters['getCurrentUser'].lastName}`
    : ''
const form = reactive<SignupFormInfo>({
  name,
  companyName: store.getters['getAccount'].companyName || '',
  email: store.getters['getCurrentUser'].email || '',
  password: '',
  vatNumber: store.getters['getAccount'].vatId || '',
  termsAndConditions: false,
  promoCode: undefined,
})

const navigateToLogin = () => {
  return router.push({
    path: '/login',
    query: { app: '', withPublicToken: 'true', ...route.query },
  })
}

const submit = async () => {
  if (!submitIsEnabled.value) return
  loading.value = true
  try {
    await userRegistration({
      email: form.email,
      password: form.password,
      name: form.name,
    })
  } catch (error) {
    console.error('failed to register user', error)
    loading.value = false
    return
  }

  const account = store.getters['getAccount']
  if (!account.signupComplete) {
    loading.value = false
    return
  }

  const livePageId = Date.now() + Math.floor(Math.random() * 1000)
  let widgetFolderName = form.companyName + Math.floor(Math.random() * 100).toString()
  widgetFolderName = widgetFolderName.replace(/\s/g, '-').toLowerCase()

  const currentUser = store.getters['getCurrentUser']
  const [firstName, ...lastName] = form.name.split(' ')

  const payload = {
    user: {
      accountType: 'ecommerce',
      email: form.email,
      vatId: form.vatNumber,
      companyName: form.companyName,
      firstName: firstName,
      lastName: lastName.join(' '),
      accountId: currentUser.id,
      signupDate: Date.now(),
      termsAccept: form.termsAndConditions,
      currency: 'dollar' as Currency,
      livePageId: livePageId,
      widgetFolderName: widgetFolderName,
      accountFromMarketplace: true,
      address: {
        country: country.value || '',
      },
    },
  }

  await createAccount(payload)
  if (form.promoCode) await usePromoCode(form.promoCode)
  loading.value = false
  emit('registered')
}
</script>

<style lang="scss" scoped>
@import '~vuetify/settings';

.signup-form {
  padding: 32px;
  color: var(--font-color-primary);
}

.form-title {
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 24px;
  line-height: 36px;
}

.password-field {
  margin-bottom: 8px;
}

.terms-and-conditions-input {
  font-size: 14px;
  margin-top: 2px;
  margin-bottom: 22px;

  :deep(.v-label) {
    font-size: 14px;
  }

  :deep(.v-selection-control__wrapper) {
    margin-right: 6px;
  }
}

.promo-code-trigger {
  margin-bottom: 24px;
}

.error-message {
  color: var(--ui-dark-gray);
  width: 100%;
  padding: 4px 8px;
  background: var(--gray-light-CC);
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 8px;

  .v-icon {
    font-size: 16px;
  }
}

.login-link {
  margin-left: 3px;
  cursor: pointer;
  font-weight: 500;
  text-decoration: underline;
}

.already-member {
  margin-top: 15px;
  font-size: 14px;
  color: #212121;
  display: flex;
  align-items: center;
  justify-content: center;
}

.green-text {
  color: #3b755f;
  cursor: pointer;
  margin-left: 3px;
}
</style>
